<template>
  <div>
    <exorderdetails-tour v-if="$store.state.tour && !loading" />

    <!-- ============ HERO ============ -->
    <section v-if="loading" class="py-5 pixelsbg bs-shadow mobile-top">
      <div class="container">
      <div class="row col col-12">
        <h1 class="text-white mb0 lh1">Loading Project....</h1>
        <br />
        <div class="hollow-dots-spinner">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </div>
        </div>
      </div>
    </section>
    <section v-else class="py-5 pixelsbggrid bs-shadow mobile-top">
      <div class="container">
      <div class="row">
        <div class="col col-12">
        <h1 v-if="project && project.orderNo != null" class="text-white mb0 lh1">
          {{ "Parts request for Order " + project.orderNo }}
        </h1>
        <h1 v-else class="text-white mb0 lh1">Loading.....</h1>
        <p v-if="project" class="text-white lh1 mb0">
          {{ project.expectations }}
        </p>
        </div>
      </div>
      </div>
    </section>

    <!-- ============ LOADING ============ -->
    <div v-if="loading || !project" class="row">
      <div class="col-12">
        <div class="flex-box-wrap">
          <div class="flex-internal">
            <atom-spinner :animation-duration="1000" :size="40" :color="'#575757'" />
            <p>Loading......</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ CONTENT ============ -->
    <!--
      Layout:
        Row 1: Info card + SSC Notes (employee) / summary sidebar (AC).
               Order entry asked for the editor to sit level with the info, so it stays in row 1.
        Row 2: Parts Requested at full width.
        Row 3: Order Lines + Associated Releases (employee only).
    -->
    <div
      v-else
      :class="employee ? 'container employee-wrap' : 'container'"
      class="mt30 mb20"
    >
      <!-- ===== ROW 1 ===== -->
      <div class="row">
        <!-- Info card -->
        <div :class="employee ? 'col-lg-7' : 'col-lg-9'" class="equal-col">
          <div id="exorderdetails-step-1" class="card-back mb10 rounded bs-shadow p-3 fill-card">
            <div class="question-header d-flex align-items-center mb5 rounded">
              <div class="d-flex number-showing w100">
                <back-button />
                <div v-if="employee" class="status ml">
                  <p v-if="project.newOrderNo !== null" class="badge badge-success">Completed</p>
                  <p v-else class="badge badge-success">Not Completed</p>
                </div>
              </div>
              <div class="pag-wrapper mlauto">
                <h2 v-if="project.requestReason">{{ project.requestReason }}</h2>
              </div>
            </div>
            <hr />

            <div class="info-top">
              <div class="row">
                <!-- Shipping -->
                <div class="col col-6">
                  <h4>Shipping</h4>

                  <h6 class="field-label mt20">
                    <span class="material-icons">corporate_fare</span> Contact Company
                  </h6>
                  <p class="text-muted mb5 lh1">
                    <small v-if="project.shippingCompany" class="text-muted">
                      {{ project.shippingCompany }}<br />
                    </small>
                    <small v-else class="text-muted">n/a<br /></small>
                    <template v-if="project.address1">
                      <small class="text-muted">{{ project.address1 }}</small><br />
                      <template v-if="project.address2">
                        <small class="text-muted">{{ project.address2 }}</small><br />
                      </template>
                      <small class="text-muted">
                        {{ project.city + ", " + project.state + " " + project.zip }}
                      </small>
                    </template>
                  </p>

                  <h6 class="field-label mt20">
                    <span class="material-icons">local_shipping</span> Carrier
                  </h6>
                  <p class="text-muted mb0 lh1">
                    <small class="text-muted">{{ project.shippingCarrier || "n/a" }}</small>
                  </p>
                </div>

                <!-- Job Information -->
                <div class="col col-6">
                  <h4>Job Information</h4>

                  <h6 class="field-label mt20">
                    <span class="material-icons">person</span> Submitted By
                  </h6>
                  <p class="text-muted mb5 lh1">
                    <small>{{ project.whoAdded || "n/a" }}</small>
                  </p>

                  <h6 class="field-label mt10">
                    <span class="material-icons">corporate_fare</span> Area Contractor Submitted
                  </h6>
                  <p class="text-muted mb5 lh1">
                    <small class="text-muted">{{ project.acName || "n/a" }}</small>
                  </p>
                </div>
              </div>

              <!-- Employee-only details -->
              <div v-if="employee">
                <hr />
                <div class="row">
                  <div class="col col-6">
                    <div class="mb10">
                      <h6 class="field-label my-0">
                        <span class="material-icons">text_snippet</span> Parts Request ID
                      </h6>
                      <small class="text-muted">{{ project.id }}</small>
                    </div>

                    <div class="mb10">
                      <h6 class="field-label my-0">
                        <span class="material-icons">text_snippet</span> Order #
                      </h6>
                      <small class="text-muted">
                        <router-link :to="`/order-details/${project.orderNo}`">
                          {{ project.orderNo }}
                        </router-link>
                      </small>
                    </div>

                    <div class="mb10">
                      <h6 class="field-label my-0">
                        <span class="material-icons">calendar_month</span> Submitted Date
                      </h6>
                      <small class="text-muted">
                        {{ $moment(project.whenAdded).format("MM-DD-YYYY") }}
                      </small>
                    </div>

                    <div>
                      <h6 class="field-label mt10 mb0">
                        <span class="material-icons">download</span> Bundling Sheet
                      </h6>
                      <small class="text-muted fw300">
                        <p v-if="mainOrder?.bundlesheet?.[0]?.doesExist" class="mb0">
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            class="pdfLink planning-link"
                            :href="'https://reportviewer.spacesaver.com/Viewer.aspx?orderid=' + project.orderNo"
                          >Report</a>
                        </p>
                        <p v-else class="mb0 text-muted">NA</p>
                      </small>
                    </div>
                  </div>

                  <div class="col col-6">
                    <div class="mb10">
                      <h6 class="field-label my-0">
                        <span class="material-icons">text_snippet</span> Salesforce Case #
                      </h6>
                      <small class="text-muted">{{ project.caseNumber || "NA" }}</small>
                    </div>

                    <div class="mb10">
                      <h6 class="field-label my-0">
                        <span class="material-icons">text_snippet</span> Expectations
                      </h6>
                      <small class="text-muted">{{ project.expectations || "NA" }}</small>
                    </div>

                    <div class="mb10">
                      <h6 class="field-label my-0">
                        <span class="material-icons">text_snippet</span> Documents
                      </h6>
                      <div v-if="docs?.length">
                        <small v-for="doc in docs" :key="doc.id" class="text-muted">
                          <div>
                            <a
                              :href="doc.documentURL"
                              target="_blank"
                              rel="noopener noreferrer"
                              data-toggle="tooltip"
                              title="If the document is an .om file, please right click to download the file."
                              @click.left="doc.documentURL.includes('.om') && rightClickWarn()"
                            >{{ doc.documentName }}</a>
                          </div>
                        </small>
                      </div>
                      <small v-else class="text-muted">NA</small>
                    </div>
                  </div>
                </div>
              </div>

              <hr />
              <h6 class="field-label my-0">
                <span class="material-icons">text_snippet</span> Notes
              </h6>
              <p class="text-muted mb5 lh1">
                <small v-if="project.notes" class="text-muted" v-html="project.notes"></small>
                <small v-else class="text-muted">n/a</small>
              </p>
            </div>
          </div>
        </div>

        <!-- SSC Notes (employee) -->
        <div v-if="employee" class="col-lg-5 equal-col">
          <div class="card-back mb10 rounded bs-shadow p-3 fill-card notes-card">
            <div class="edit-notes">
              <h4>SSC Notes</h4>
            </div>
            <hr />

            <form class="notes-form" @submit.prevent="updateNotes">
              <div class="form-group">
                <label for="ordernum">Order Number</label>
                <input
                  v-if="project.newOrderNo"
                  id="ordernum"
                  type="text"
                  class="form-control"
                  :value="project.newOrderNo"
                  disabled
                />
                <input
                  v-else
                  id="ordernum"
                  v-model="tempOrderNo"
                  type="text"
                  class="form-control"
                  placeholder="Enter new order number"
                />
              </div>

              <div class="form-group editor-group">
                <label>Notes</label>
                <div class="document-editor">
                  <QuillEditor
                    ref="quillRef"
                    :model-value="editorContent"
                    @update:model-value="editorContent = $event"
                    theme="snow"
                    :options="editorOptions"
                    @ready="onEditorReady"
                  />
                </div>
              </div>

              <div class="notes-footer">
                <div class="notes-checks">
                  <label class="custom-checkbox">
                    <input v-model="project.isComplete" type="checkbox" />
                    <span class="checkmark"></span>
                    Mark as Coded
                  </label>
                  <label class="custom-checkbox">
                    <input v-model="project.inProgress" type="checkbox" />
                    <span class="checkmark"></span>
                    Mark as In Progress
                  </label>
                </div>
                <button type="submit" class="mb0 lh1 list-btn dib d-flex center">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Summary sidebar (AC) -->
        <div v-else class="col-lg-3 equal-col">
          <div class="card-back mb10 rounded bs-shadow p-3 fill-card">
            <ul class="list-group mb-3">
              <li class="list-group-item d-flex justify-content-between lh-condensed">
                <div>
                  <h6 class="my-0"><i class="fas fa-file-alt"></i> Parts Request ID</h6>
                  <small class="text-muted">{{ project.id }}</small>
                </div>
              </li>
              <li class="list-group-item d-flex justify-content-between lh-condensed">
                <div>
                  <h6 class="my-0"><i class="fas fa-file-alt"></i> Order #</h6>
                  <small class="text-muted">
                    <router-link :to="`/order-details/${project.orderNo}`">
                      {{ project.orderNo }}
                    </router-link>
                  </small>
                </div>
              </li>
              <li class="list-group-item d-flex justify-content-between lh-condensed">
                <div>
                  <h6 class="my-0"><i class="fas fa-file-alt"></i> Salesforce Case #</h6>
                  <small class="text-muted">{{ project.caseNumber || "NA" }}</small>
                </div>
              </li>
              <li
                v-if="project.newOrderNo"
                class="list-group-item d-flex justify-content-between lh-condensed"
              >
                <div>
                  <h6 class="my-0"><i class="fas fa-file-alt"></i> New Order #</h6>
                  <small class="text-muted">
                    <router-link :to="`/order-details/${project.newOrderNo}`">
                      {{ project.newOrderNo }}
                    </router-link>
                  </small>
                </div>
              </li>
              <li class="list-group-item d-flex justify-content-between lh-condensed">
                <div>
                  <h6 class="my-0"><i class="fas fa-file-alt"></i> Submitted Date</h6>
                  <small class="text-muted">
                    {{ $moment(project.whenAdded).format("MM-DD-YYYY") }}
                  </small>
                </div>
              </li>
              <li class="list-group-item d-flex justify-content-between lh-condensed">
                <div>
                  <h6 class="my-0"><i class="fas fa-file-alt"></i> Expectations</h6>
                  <small class="text-muted">{{ project.expectations || "NA" }}</small>
                </div>
              </li>
              <li class="list-group-item d-flex justify-content-between lh-condensed">
                <div>
                  <h6 class="my-0"><i class="fas fa-file-alt"></i> Documents</h6>
                  <div v-if="docs?.length">
                    <small v-for="doc in docs" :key="doc.id" class="text-muted">
                      <div>
                        <a
                          :href="doc.documentURL"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          data-toggle="tooltip"
                          title="If the document is an .om file, please right click to download the file."
                          @click.left="doc.documentURL.includes('.om') && rightClickWarn()"
                        >{{ doc.documentName }}</a>
                      </div>
                    </small>
                  </div>
                  <small v-else class="text-muted">NA</small>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- ===== ROW 2: Parts Requested (full width) ===== -->
      <!-- ===== ROW 2: Parts Requested (full width) ===== -->
<div class="row">
  <div class="col-12">
    <div id="exorderdetails-step-2" class="card-back mb10 rounded bs-shadow p-3">
      <div class="d-flex align-items-center">
        <h4 class="mb0">Parts Requested</h4>
        <span v-if="parts?.length" class="badge badge-success ml">{{ parts.length }}</span>
      </div>
      <hr />

      <form v-if="parts?.length" @submit.prevent="savePartCorrections">
        <div class="table-responsive">
          <table class="table table-striped table-bordered lines-table mb0">
            <thead>
              <tr>
                <th scope="col" class="col-part">Part # Requested</th>
                <!--
                <th v-if="showActualCol" scope="col" class="col-actual">Actual Part #</th>
                -->
                <th scope="col" class="col-qty" style="width: 5%;">Qty</th>
                <th scope="col" class="nowrap" style="width: 12%;">Color</th>
                <th scope="col" style="width: 12%;">Reason</th>
                <th scope="col" class="col-notes">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(line, index) in parts"
                :key="line.id ?? index"
                :class="{ 'part-on-order': isOnOrder(effectivePart(line)) }"
              >
                <!-- Original stays as submitted; struck through once corrected -->
                <th scope="row" class="part-cell">
                  <small
                    :class="[
                      isOnOrder(effectivePart(line)) ? 'part-on-order-text' : 'text-muted',
                      { superseded: line.actualPartNumber }
                    ]"
                  >
                    {{ line.partNumber }}
                  </small>
                </th>

                <!----
                 <td v-if="showActualCol" class="actual-cell">
                  <input
                    v-if="employee"
                    v-model.trim="partDrafts[line.id]"
                    type="text"
                    class="form-control form-control-sm"
                    :class="{ 'is-changed': isPartDirty(line) }"
                    placeholder="Same as requested"
                  />
                  <small v-else class="actual-part">{{ line.actualPartNumber }}</small>
                </td>
                -->
               

                <td><small class="text-muted">{{ line.quantity }}</small></td>
                <td class="nowrap"><small class="text-muted">{{ line.color }}</small></td>
                <td><small class="text-muted">{{ line.reason }}</small></td>
                <td><small class="text-muted">{{ line.notes }}</small></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!--
         <div v-if="employee" class="parts-footer">
          <small v-if="dirtyParts.length" class="text-muted">
            {{ dirtyParts.length }} unsaved change{{ dirtyParts.length > 1 ? "s" : "" }}
          </small>
          <button
            type="button"
            class="btn btn-link btn-sm"
            :disabled="!dirtyParts.length || savingParts"
            @click="resetPartDrafts"
          >
            Reset
          </button>
          <button
            type="submit"
            class="mb0 lh1 list-btn dib d-flex center"
            :disabled="!dirtyParts.length || savingParts"
          >
            {{ savingParts ? "Saving..." : "Save Corrections" }}
          </button>
        </div>
        
        -->
       
      </form>
      <small v-else class="text-muted">No Parts</small>
    </div>
  </div>
</div>

      <!-- ===== ROW 3: Order Lines + Releases ===== -->
      <div class="row">
        <div :class="employee ? 'col-lg-9' : 'col-12'">
          <div id="exorderdetails-step-3" class="card-back mb10 rounded bs-shadow p-3">
            <h4>Order Lines on {{ project.orderNo }}</h4>
            <hr />

            <div v-if="orderLines.length" class="table-responsive">
              <table class="table table-striped table-bordered lines-table mb0">
                <thead>
                  <tr>
                    <th class="nowrap">Part #</th>
                    <th class="col-qty">Qty</th>
                    <th class="nowrap">Color</th>
                    <th class="nowrap">List Price</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(line, index) in orderLines"
                    :key="index"
                    :class="{ 'part-on-order': isRequested(line.catalogNo) }"
                  >
                    <td class="nowrap">
                      <small :class="isRequested(line.catalogNo) ? 'part-on-order-text' : 'text-muted'">
                        {{ line.catalogNo }}
                      </small>
                    </td>
                    <td><small class="text-muted">{{ line.buyQty }}</small></td>
                    <td class="nowrap"><small class="text-muted">{{ line.color }}</small></td>
                    <td class="nowrap"><small class="text-muted">{{ formatPrice(line.cplPrice) }}</small></td>
                    <td><small class="text-muted">{{ line.catalogDesc }}</small></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <h6 v-else class="my-0 text-muted">No Lines Available</h6>
          </div>
        </div>

        <div v-if="employee" class="col-lg-3">
          <div class="card-back mb10 rounded bs-shadow p-3 releases-card">
            <div class="d-flex align-items-center">
              <h4 class="mb0">Assoc. Releases</h4>
              <span class="badge badge-success ml">{{ releaseList?.length || 0 }}</span>
            </div>

            <input
              v-if="releaseList?.length > 10"
              v-model="releaseFilter"
              type="text"
              class="form-control form-control-sm mt10"
              placeholder="Filter releases..."
            />
            <hr />

            <div v-if="filteredReleases.length" class="release-chips">
              <router-link
                v-for="release in filteredReleases"
                :key="release.orderNo"
                :to="`/order-details/${release.orderNo}`"
                :class="['release-chip', { current: release.orderNo === project.orderNo }]"
              >
                {{ release.orderNo }}
              </router-link>
            </div>
            <small v-else class="text-muted">
              {{ releaseFilter ? "No matches" : "No releases" }}
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { AtomSpinner } from "epic-spinners";
import BackButton from '@/components/Buttons/BackButton.vue';
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import exorderdetailsTour from "../components/Tours/exorderdetailsTour.vue";
import { formatCurrency } from '../utils/utils';

const auth = {
  headers: {
    Authorization: "Bearer " + localStorage.getItem("SSCUSER"),
    //Authorization: "Bearer " + 'eyJhbGci7yJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJVc2VySWQiOiIyREQyQzk4NC1BM0RELTQ5MjMtQUM1Mi01QjA0MzgyOTg5QUQiLCJVc2VyTmFtZSI6ImpvYXMiLCJFbWFpbCI6ImpvYXNAc3BhY2VzYXZlci5jb20iLCJGaXJzdE5hbWUiOiJKb3NodWEiLCJMYXN0TmFtZSI6Ik9hcyIsIkNvbXBhbnkiOiJTcGFjZXNhdmVyIENvcnBvcmF0aW9uIiwiRGVwYXJ0bWVudCI6IklTIiwiUGhvbmVOdW1iZXIiOiI5MjA1NjM2MzYyIiwiQ2VsbFBob25lIjoiIiwiRW1haWxEb21haW4iOiJzcGFjZXNhdmVyLmNvbSIsIkFDSWQiOlswXSwiUmVwSWQiOjAsIlJvbGVzIjpbInNzYyIsImRvY2xpYmFkbWluIiwiYWRtaW4iXSwiYWRtaW4iOnRydWUsInNzY2VtcGxveWVlIjp0cnVlLCJkb2NsaWJhZG1pbiI6dHJ1ZSwiTGFzdEFjdGl2aXR5IjoiMjAyMS0wNC0yMlQxMzo1MzoxNy4yMTNaIiwiaWF0IjoxNjE5MDk5NjM5LCJleHAiOjE2MjE2OTE2Mzl9.gWs9nahp7NAzljgV0jhS27setYqj-fo87lmaBkzj_D0'
  },
};



export default {
  name: "ExpeditedOrderDetails",
  components: {
    AtomSpinner,
    BackButton,
    QuillEditor,
    exorderdetailsTour,
  },
  data() {
    return {
      ordermanageapi: import.meta.env.VITE_APP_ORDER_MANAGE_API,
      projectId: this.$route.params.id,
      // editor: ClassicEditor,
      project: null,
      mainOrder: null,
      releaseList: null,
      parts: null,
      docs: null,
      loading: false,
      processing: false,
      preprod: false,
      inprod: false,
      shipped: false,
      open: "",
      pdfPreview: "",
      requestedDoc: "",
      sscNotesStripped: null,
      editing: false,
      editorContent: '',
      releaseFilter: '',
      editorData: "<p>Content of the editor.</p>",
      editorConfig: {
        toolbar: [
          'bold', 'italic', '|',
          'bulletedList', 'numberedList', '|',
          'undo', 'redo'
        ]
      },
     editorOptions: {
      theme: 'snow',
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ 'header': 1 }, { 'header': 2 }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'align': [] }],
          ['clean']
        ]
      }
    },
      tempOrderNo: null,
      partDrafts: {},
      savingParts: false,
    };
  },
  methods: {


    firstLoad: function () {
      console.log(this.$route.params.id);
      this.loading = true;
      this.$store.commit("setActivePage", "order-manage");
      //return axios.get(this.endPointCompute, auth).then((response) => {
      return axios
        .get(`${this.ordermanageapi}/expeditedorders/${this.projectId}`, auth)
        .then((response) => {
          console.log(response)
          //console.log(response.data[0]);
          this.project = response.data[0];
          this.parts = response.data[0].eoParts;
          this.resetPartDrafts();
          this.docs = response.data[0].eoDocuments;
          this.editorContent = this.project.sscNotes || '';
          
          if (this.project){
              // order.orderDetail[0].orderNo
              return axios
              .get(`${this.ordermanageapi}/order/${this.project.orderNo}`, auth)
              .then((response) => {
                // console.log(response);
                if (response.status == 204) {
                  // console.log("no content");
                  this.loading = false;
                  this.notFound = true;
                } else {
                  this.getReleaseNumbers(this.NumberBeforeHyphen(this.project.orderNo));
                  this.mainOrder = response.data;
                  this.loading = false;
                  document
                    .getElementById("mobileScrollTarget")
                    .scrollIntoView({ behavior: "smooth" });
                }
                // this.orderLinesCSV();
              });
            }
            
          /*
          let regex = /<[^>]*(>|$)|&nbsp;|&zwnj;|&raquo;|&laquo;|&gt;/g;
          if (this.project != null) {
            let newNotes = this.project.sscNotes.replace(regex, "");
            console.log("new notes " + newNotes);
            this.sscNotesStripped = newNotes;
          }
          */
          this.loading = false;

          document
            .getElementById("mobileScrollTarget")
            .scrollIntoView({ behavior: "smooth" });
        });
    },

    normalizePart(val) {
    return String(val ?? "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "");
  },
  effectivePart(line) {
  return line.actualPartNumber || line.partNumber;
},
resetPartDrafts() {
  this.partDrafts = Object.fromEntries(
    (this.parts || []).map((p) => [p.id, p.actualPartNumber || ""])
  );
},
isPartDirty(line) {
  return (this.partDrafts[line.id] ?? "") !== (line.actualPartNumber || "");
},
async savePartCorrections() {
  const changed = this.dirtyParts;
  if (!changed.length) return;

  const axiosConfig = {
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      Authorization: "Bearer " + localStorage.getItem("SSCUSER"),
    },
  };

  this.savingParts = true;
  try {
    await Promise.all(
      changed.map((p) =>
        axios.patch(
          `${this.ordermanageapi}/expeditedorders/parts/${p.id}`,
          [{ op: "replace", path: "/ActualPartNumber", value: this.partDrafts[p.id] || null }],
          axiosConfig
        )
      )
    );
    // Only touches actualPartNumber; partNumber is never modified
    changed.forEach((p) => {
      p.actualPartNumber = this.partDrafts[p.id] || null;
    });
    this.$toast.success("Part corrections saved");
  } catch (err) {
    this.$toast.error("Sorry, there was an issue saving part corrections");
  } finally {
    this.savingParts = false;
  }
},
  getReleaseNumbers(value) {
    let url = `${this.ordermanageapi}/order?search=${value}` 
      return axios.get(url, auth).then((response) => {
        this.releaseList = response.data;
    });
  },
  NumberBeforeHyphen(value) {
    return String(value ?? '').split('-')[0]
  },
  isOnOrder(partNumber) {
    const key = this.normalizePart(partNumber);
    return key !== "" && this.orderLinePartNumbers.has(key);
  },
  isRequested(catalogNo) {
    const key = this.normalizePart(catalogNo);
    return key !== "" && this.requestedPartNumbers.has(key);
  },

    formatPrice(amount) {
      return formatCurrency(amount);
    },

    handleEditorUpdate(newContent) {
      console.log("Editor updated with:", newContent);   // ← Debug
      this.editorContent = newContent;
    },


    onReady(editor) {
          const toolbarContainer = document.querySelector(  '.document-editor__toolbar' );
          toolbarContainer.appendChild( editor.ui.view.toolbar.element );
    },

    onEditorReady(editor) {
      console.log('✅ QuillEditor Ready');
      this.$nextTick(() => {
        if (this.$refs.quillRef) {
          this.$refs.quillRef.setHTML(this.editorContent);
        }
      });
    },

    downloadWithJS(href){
      //window.open(file, '_blank');
      var anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = href;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    },
    rightClickWarn(){
      this.$toast.success("Please right click to download .om files");
      /*
      this.$noty.success(
        "Please right click to download .om files"
      );
      */
      
    },

    forceDownload(blob, filename) {
      var a = document.createElement('a');
      a.download = filename;
      a.href = blob;
      // For Firefox https://stackoverflow.com/a/32226068
      document.body.appendChild(a);
      a.click();
      a.remove();
    },


    downloadWithAxios(url, title) {
      //console.log(url);
      axios({
        method: 'get',
        url,
        responseType: 'arraybuffer',
      })
        .then((response) => {
          this.forceFileDownload(response, title)
        })
        .catch(() => console.log('error occured'))
    },

    makeActive(item) {
      //console.log(item);
      if (this.open === item) {
        this.open = "";
      } else {
        this.open = item;
      }
    },
    turnonEdit() {
      this.editing = true;
    },
    getDocument(link) {
      //console.log("link: " + link);
      /*
      window.open(
        `http://spacenet2.spacesaver.com/ordermanage/${link}`,
        "_blank"
      );
      */
      window.open(`{link}`, "_blank");
    },
    updateNotes() {
      if (this.$refs.quillRef) {
        const currentContent = this.$refs.quillRef.getHTML();
        this.editorContent = currentContent;
        if (this.project) {
          this.project.sscNotes = currentContent;
        }
      }

      console.log("Saving SSC Notes:", this.editorContent);

      let axiosConfig = {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: "Bearer " + localStorage.getItem("SSCUSER"),
        },
      };
      let theDATA = [
        {
          op: "replace",
          path: "/newOrderID",
          value: this.project.newOrderNo
            ? this.project.newOrderNo
            : this.tempOrderNo,
        },
        {
          op: "replace",
          path: "/NewIDEnteredBy",
          value: this.project.newIdEnteredBy,
        },
        {
          op: "replace",
          path: "/SSCNotes",
          value: this.editorContent,
        },
        {
          op:"replace",
          path:"/IsComplete",
          value: this.project.isComplete,
        },
        
        {
          op:"replace",
          path:"/InProgress",
          value: this.project.inProgress,
        }
        
      ];

      return axios
        .patch(
          `${this.ordermanageapi}/expeditedorders/${this.projectId}`,
          theDATA,
          axiosConfig
        )
        .then((response) => {
          //console.log(response);
          if (this.tempOrderNo) {
            this.project.newOrderNo = this.tempOrderNo;
          }
          this.$toast.success("Successfully updated expedited order!");
          //console.log(response.data.thisStream);
          //window.open(response.data.url, "_blank");

          //console.log(response.data[0]);
          /*
          this.project = response.data[0];
          this.parts = response.data[0].eoParts;
          this.docs = response.data[0].eoDocuments;
          this.loading = false;
*/
        })
        .catch((err) => {
          /*
          this.$noty.error(
            "Sorry, there was an issue updating the expedited order"
          );
          */
          this.$toast.error("Sorry, there was an issue updating the expedited order");
        });
    },
  },
  computed: {
    orderLines() {
      return this.mainOrder?.orderDetail?.[0]?.orderLines || [];
    },
    sortedReleases() {
      const n = (r) => parseInt(String(r.orderNo).split('-').pop(), 10);
      return [...(this.releaseList || [])].sort((a, b) => n(a) - n(b));
    },
    filteredReleases() {
      const f = this.releaseFilter.trim().toLowerCase();
      return f
        ? this.sortedReleases.filter((r) => String(r.orderNo).toLowerCase().includes(f))
        : this.sortedReleases;
    },
    dirtyParts() {
  return (this.parts || []).filter((p) => this.isPartDirty(p));
},
showActualCol() {
  // Employees always see it; ACs only when a correction exists
  return this.employee || (this.parts || []).some((p) => p.actualPartNumber);
},
requestedPartNumbers() {
  return new Set(
    (this.parts || [])
      .map((p) => this.normalizePart(this.effectivePart(p)))
      .filter(Boolean)
  );
},
    orderLinePartNumbers() {
      const lines = this.mainOrder?.orderDetail?.[0]?.orderLines || [];
      return new Set(
        lines
          .map((l) => this.normalizePart(l.catalogNo))
          .filter(Boolean)
      );
    },
    activeNav() {
      return this.$store.state.activePage;
    },
    strippedContent() {
      //let regex = /(<([^>]+)>)/gi;
      let regex = /<[^>]*(>|$)|&nbsp;|&zwnj;|&raquo;|&laquo;|&gt;/g;
      if (this.project != null) {
        let newNotes = this.project.sscNotes.replace(regex, "");
        //console.log("new notes " + newNotes);
        this.sscNotesStripped = newNotes;
      }
    },
    employee() {
      return this.$store.getters.isSpacesaverEmployee;
    },

    colorGetter() {
      return this.$store.state.colors;
    },

    getUsername() {
      return this.$store.state.user;
    },
    getUserID() {
      this.project.newIdEnteredBy = this.$store.state.user.UserId;
    },
  },
  created() {
    this.$store.commit("showNavigation");

    // console.log(this.$route.path);
  },
  mounted() {
    document
      .getElementById("mobileScrollTarget")
      .scrollIntoView({ behavior: "smooth" });
    this.firstLoad().then(function () {});
  },
};
</script>
<style>

.cke_editable p {
  margin: 0 0!important;
  padding: 0 0!important;
}

.beef-tips{
  background: pink;
}
body > .cke_editable {
  background: pink;
}

.ck-content p {
  margin: 0!important;
  padding: 0!important;
}
.ck-content .text-tiny {
    margin: 0!important;
  padding: 0!important;
}
/* ckeditor5-font/theme/fontsize.css */
.ck-content .text-small {
   margin: 0!important;
  padding: 0!important;
}
/* ckeditor5-font/theme/fontsize.css */
.ck-content .text-big {
    margin: 0!important;
  padding: 0!important;
}
/* ckeditor5-font/theme/fontsize.css */
.ck-content .text-huge {
    margin: 0!important;
  padding: 0!important;
}
#cke_editor1 p {
  margin: 0;
}
.document-editor .ck-content p {
        font-size: 1em;
        line-height: 1.63em;
        padding-top: 0;
        margin-bottom: 0;
        color: pink!important;
  }
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/*
 * CKEditor 5 (v28.0.0) content styles.
 * Generated on Mon, 07 Jun 2021 06:33:15 GMT.
 * For more information, check out https://ckeditor.com/docs/ckeditor5/latest/builds/guides/integration/content-styles.html
 */

:root {
  --ck-color-mention-background: hsla(341, 100%, 30%, 0.1);
  --ck-color-mention-text: hsl(341, 100%, 30%);
  --ck-color-table-caption-background: hsl(0, 0%, 97%);
  --ck-color-table-caption-text: hsl(0, 0%, 20%);
  --ck-highlight-marker-blue: hsl(201, 97%, 72%);
  --ck-highlight-marker-green: hsl(120, 93%, 68%);
  --ck-highlight-marker-pink: hsl(345, 96%, 73%);
  --ck-highlight-marker-yellow: hsl(60, 97%, 73%);
  --ck-highlight-pen-green: hsl(112, 100%, 27%);
  --ck-highlight-pen-red: hsl(0, 85%, 49%);
  --ck-image-style-spacing: 1.5em;
  --ck-todo-list-checkmark-size: 16px;
}

/* ckeditor5-highlight/theme/highlight.css */
.ck-content .marker-yellow {
  background-color: var(--ck-highlight-marker-yellow);
}
/* ckeditor5-highlight/theme/highlight.css */
.ck-content .marker-green {
  background-color: var(--ck-highlight-marker-green);
}
/* ckeditor5-highlight/theme/highlight.css */
.ck-content .marker-pink {
  background-color: var(--ck-highlight-marker-pink);
}
/* ckeditor5-highlight/theme/highlight.css */
.ck-content .marker-blue {
  background-color: var(--ck-highlight-marker-blue);
}
/* ckeditor5-highlight/theme/highlight.css */
.ck-content .pen-red {
  color: var(--ck-highlight-pen-red);
  background-color: transparent;
}
/* ckeditor5-highlight/theme/highlight.css */
.ck-content .pen-green {
  color: var(--ck-highlight-pen-green);
  background-color: transparent;
}
/* ckeditor5-font/theme/fontsize.css */
.ck-content .text-tiny {
  font-size: 0.7em;
}
/* ckeditor5-font/theme/fontsize.css */
.ck-content .text-small {
  font-size: 0.85em;
}
/* ckeditor5-font/theme/fontsize.css */
.ck-content .text-big {
  font-size: 1.4em;
}
/* ckeditor5-font/theme/fontsize.css */
.ck-content .text-huge {
  font-size: 1.8em;
}
/* ckeditor5-image/theme/imagestyle.css */
.ck-content .image-style-side {
  float: right;
  margin-left: var(--ck-image-style-spacing);
  max-width: 50%;
}
/* ckeditor5-image/theme/imagestyle.css */
.ck-content .image-style-align-left {
  float: left;
  margin-right: var(--ck-image-style-spacing);
}
/* ckeditor5-image/theme/imagestyle.css */
.ck-content .image-style-align-center {
  margin-left: auto;
  margin-right: auto;
}
/* ckeditor5-image/theme/imagestyle.css */
.ck-content .image-style-align-right {
  float: right;
  margin-left: var(--ck-image-style-spacing);
}
/* ckeditor5-image/theme/image.css */
.ck-content .image {
  display: table;
  clear: both;
  text-align: center;
  margin: 1em auto;
}
/* ckeditor5-image/theme/image.css */
.ck-content .image img {
  display: block;
  margin: 0 auto;
  max-width: 100%;
  min-width: 50px;
}
/* ckeditor5-image/theme/imagecaption.css */
.ck-content .image > figcaption {
  display: table-caption;
  caption-side: bottom;
  word-break: break-word;
  color: hsl(0, 0%, 20%);
  background-color: hsl(0, 0%, 97%);
  padding: 0.6em;
  font-size: 0.75em;
  outline-offset: -1px;
}
/* ckeditor5-image/theme/imageresize.css */
.ck-content .image.image_resized {
  max-width: 100%;
  display: block;
  box-sizing: border-box;
}
/* ckeditor5-image/theme/imageresize.css */
.ck-content .image.image_resized img {
  width: 100%;
}
/* ckeditor5-image/theme/imageresize.css */
.ck-content .image.image_resized > figcaption {
  display: block;
}
/* ckeditor5-language/theme/language.css */
.ck-content span[lang] {
  font-style: italic;
}
/* ckeditor5-code-block/theme/codeblock.css */
.ck-content pre {
  padding: 1em;
  color: hsl(0, 0%, 20.8%);
  background: hsla(0, 0%, 78%, 0.3);
  border: 1px solid hsl(0, 0%, 77%);
  border-radius: 2px;
  text-align: left;
  direction: ltr;
  tab-size: 4;
  white-space: pre-wrap;
  font-style: normal;
  min-width: 200px;
}
/* ckeditor5-code-block/theme/codeblock.css */
.ck-content pre code {
  background: unset;
  padding: 0;
  border-radius: 0;
}
/* ckeditor5-horizontal-line/theme/horizontalline.css */
.ck-content hr {
  margin: 15px 0;
  height: 4px;
  background: hsl(0, 0%, 87%);
  border: 0;
}
/* ckeditor5-html-embed/theme/htmlembed.css */
.ck-content .raw-html-embed {
  margin: 1em auto;
  min-width: 15em;
  font-style: normal;
}
/* ckeditor5-block-quote/theme/blockquote.css */
.ck-content blockquote {
  overflow: hidden;
  padding-right: 1.5em;
  padding-left: 1.5em;
  margin-left: 0;
  margin-right: 0;
  font-style: italic;
  border-left: solid 5px hsl(0, 0%, 80%);
}
/* ckeditor5-block-quote/theme/blockquote.css */
.ck-content[dir="rtl"] blockquote {
  border-left: 0;
  border-right: solid 5px hsl(0, 0%, 80%);
}
/* ckeditor5-basic-styles/theme/code.css */
.ck-content code {
  background-color: hsla(0, 0%, 78%, 0.3);
  padding: 0.15em;
  border-radius: 2px;
}
/* ckeditor5-table/theme/table.css */
.ck-content .table {
  margin: 1em auto;
  display: table;
}
/* ckeditor5-table/theme/table.css */
.ck-content .table table {
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;
  height: 100%;
  border: 1px double hsl(0, 0%, 70%);
}
/* ckeditor5-table/theme/table.css */
.ck-content .table table td,
.ck-content .table table th {
  min-width: 2em;
  padding: 0.4em;
  border: 1px solid hsl(0, 0%, 75%);
}
/* ckeditor5-table/theme/table.css */
.ck-content .table table th {
  font-weight: bold;
  background: hsla(0, 0%, 0%, 5%);
}
/* ckeditor5-table/theme/table.css */
.ck-content[dir="rtl"] .table th {
  text-align: right;
}
/* ckeditor5-table/theme/table.css */
.ck-content[dir="ltr"] .table th {
  text-align: left;
}
/* ckeditor5-table/theme/tablecaption.css */
.ck-content .table > figcaption {
  display: table-caption;
  caption-side: top;
  word-break: break-word;
  text-align: center;
  color: var(--ck-color-table-caption-text);
  background-color: var(--ck-color-table-caption-background);
  padding: 0.6em;
  font-size: 0.75em;
  outline-offset: -1px;
}
/* ckeditor5-page-break/theme/pagebreak.css */
.ck-content .page-break {
  position: relative;
  clear: both;
  padding: 5px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
/* ckeditor5-page-break/theme/pagebreak.css */
.ck-content .page-break::after {
  content: "";
  position: absolute;
  border-bottom: 2px dashed hsl(0, 0%, 77%);
  width: 100%;
}
/* ckeditor5-page-break/theme/pagebreak.css */
.ck-content .page-break__label {
  position: relative;
  z-index: 1;
  padding: 0.3em 0.6em;
  display: block;
  text-transform: uppercase;
  border: 1px solid hsl(0, 0%, 77%);
  border-radius: 2px;
  font-family: Helvetica, Arial, Tahoma, Verdana, Sans-Serif;
  font-size: 0.75em;
  font-weight: bold;
  color: hsl(0, 0%, 20%);
  background: hsl(0, 0%, 100%);
  box-shadow: 2px 2px 1px hsla(0, 0%, 0%, 0.15);
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
/* ckeditor5-media-embed/theme/mediaembed.css */
.ck-content .media {
  clear: both;
  margin: 1em 0;
  display: block;
  min-width: 15em;
}
/* ckeditor5-list/theme/todolist.css */
.ck-content .todo-list {
  list-style: none;
}
/* ckeditor5-list/theme/todolist.css */
.ck-content .todo-list li {
  margin-bottom: 5px;
}
/* ckeditor5-list/theme/todolist.css */
.ck-content .todo-list li .todo-list {
  margin-top: 5px;
}
/* ckeditor5-list/theme/todolist.css */
.ck-content .todo-list .todo-list__label > input {
  -webkit-appearance: none;
  display: inline-block;
  position: relative;
  width: var(--ck-todo-list-checkmark-size);
  height: var(--ck-todo-list-checkmark-size);
  vertical-align: middle;
  border: 0;
  left: -25px;
  margin-right: -15px;
  right: 0;
  margin-left: 0;
}
/* ckeditor5-list/theme/todolist.css */
.ck-content .todo-list .todo-list__label > input::before {
  display: block;
  position: absolute;
  box-sizing: border-box;
  content: "";
  width: 100%;
  height: 100%;
  border: 1px solid hsl(0, 0%, 20%);
  border-radius: 2px;
  transition: 250ms ease-in-out box-shadow, 250ms ease-in-out background,
    250ms ease-in-out border;
}
/* ckeditor5-list/theme/todolist.css */
.ck-content .todo-list .todo-list__label > input::after {
  display: block;
  position: absolute;
  box-sizing: content-box;
  pointer-events: none;
  content: "";
  left: calc(var(--ck-todo-list-checkmark-size) / 3);
  top: calc(var(--ck-todo-list-checkmark-size) / 5.3);
  width: calc(var(--ck-todo-list-checkmark-size) / 5.3);
  height: calc(var(--ck-todo-list-checkmark-size) / 2.6);
  border-style: solid;
  border-color: transparent;
  border-width: 0 calc(var(--ck-todo-list-checkmark-size) / 8)
    calc(var(--ck-todo-list-checkmark-size) / 8) 0;
  transform: rotate(45deg);
}
/* ckeditor5-list/theme/todolist.css */
.ck-content .todo-list .todo-list__label > input[checked]::before {
  background: hsl(126, 64%, 41%);
  border-color: hsl(126, 64%, 41%);
}
/* ckeditor5-list/theme/todolist.css */
.ck-content .todo-list .todo-list__label > input[checked]::after {
  border-color: hsl(0, 0%, 100%);
}
/* ckeditor5-list/theme/todolist.css */
.ck-content .todo-list .todo-list__label .todo-list__label__description {
  vertical-align: middle;
}
/* ckeditor5-mention/theme/mention.css */
.ck-content .mention {
  background: var(--ck-color-mention-background);
  color: var(--ck-color-mention-text);
}
@media print {
  /* ckeditor5-page-break/theme/pagebreak.css */
  .ck-content .page-break {
    padding: 0;
  }
  /* ckeditor5-page-break/theme/pagebreak.css */
  .ck-content .page-break::after {
    display: none;
  }
}
@media all and (min-width: 992px) {
  .flex-box-wrap {
    height: 200px;
    padding: 150px 0 125px 0;
    margin: 0;
  }
}

@media all and (max-width: 991px) {
  .flex-box-wrap {
    height: 100px;
    padding: 75px 0 50px 0;
    margin: 0;
    justify-content: center;
    align-items: center;
  }
}

.card-back {
  background: #fff;
}
.article {
  background-color: #fff;
  line-height: 44px;
  padding: 20px;
  margin-bottom: 5px;
  border-top: 0;
}
.top-wrap {
  display: flex;
  align-items: flex-start;
}
.question-bottom {
  align-items: flex-end;
}
.question-header {
  background: #fff;
  color: #575757;
  display: flex !important;
}
.center {
  align-items: center;
  justify-content: center;
}
.tab-container {
  background: #f2f2f3;
  padding: 10px;
  border-radius: 4px;
}
.nav-tabs {
  border-bottom: none;
}
.nav-tabs .nav-link {
  border: none;
}
#nav-tabContent {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
}
.d-flex {
  display: flex;
}
.ml {
  margin-left: auto;
}
/*
.comments:nth-child(even) {
  background: #efefef;
  padding: 10px;
  margin-bottom: 1rem;
  border-radius: 4px;
}
*/
.comments {
  border-bottom: 1px solid #efefef;
  margin-bottom: 10px;
}
.comments p {
  margin-bottom: 10px;
}
.comments:last-child {
  border-bottom: none;
  margin-bottom: 0;
}
.comments:last-child p {
  margin-bottom: 0;
}
.tab-container a {
  color: #429da8;
}
.mt30 {
  margin-top: 30px;
}
.badge {
  display: inline-block;
  padding: 0.5em 0.8em;
}
.progtrckr {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

.progtrckr li {
  display: inline-block;
  text-align: center;
  line-height: 3.5em;
}

.progtrckr[data-progtrckr-steps="2"] li {
  width: 25%;
}
.progtrckr[data-progtrckr-steps="3"] li {
  width: 25%;
}
.progtrckr[data-progtrckr-steps="4"] li {
  width: 25%;
}
.progtrckr[data-progtrckr-steps="5"] li {
  width: 25%;
}

.progtrckr li.progtrckr-done {
  color: #fff;
  border-bottom: 4px solid #fff;
}
.progtrckr li.progtrckr-todo {
  color: #3e4651;
  border-bottom: 4px solid #3e4651;
}

.progtrckr li:after {
  content: "\00a0\00a0";
  margin-left: 28px;
  /* this is the text*/
}
.progtrckr li:before {
  position: relative;
  bottom: -2.5em;
  float: left;
  left: calc(50% - 20px);
  line-height: 1em;
}
.progtrckr li.progtrckr-done:before {
  content: "\2713";
  color: #3e4651;
  background-color: #fff;
  height: 40px;
  width: 40px;
  line-height: 40px;
  border: none;
  border-radius: 40px;
}
.progtrckr li.progtrckr-todo:before {
  /*
  content: "\039F";
  color: #fff;
  background-color: #fff;
  font-size: 2.2em;
  bottom: -1.2em;
  */
  content: "\2713";
  color: #3e4651;
  background-color: #3e4651;
  height: 2.2em;
  width: 2.2em;
  line-height: 2.2em;
  border: none;
  border-radius: 2.2em;
}
/* trackker titles */
.tracker-title-wrap {
  margin-top: 15px;
}
.trckr-title {
  padding-left: 0;
}
.trckr-title li {
  display: inline-block;
  text-align: center;
  line-height: 3.5em;
  width: 25%;
  color: #fff;
}
.trckr-title-done {
  color: #fff !important;
}
.trckr-title-todo {
  color: #3e4651 !important;
}
.badge-success {
  color: #fff;
  background-color: #3e4651;
}
@media all and (max-width: 700px) {
  .trckr-title li {
    font-size: 12px;
  }
}
.up {
  text-transform: uppercase;
}
.lines-table td,
.lines-table th {
  padding: 5px;
}
.small,
small {
  font-size: 90% !important;
}

.table td .small,
small {
  font-size: 90% !important;
}
.bol {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  background: #fff;
  padding: 10px;
  list-style-type: none;
  /*border: 1px solid #dee2e6;*/
  background: #efefef;
  margin-bottom: 5px;
  transition: background 0.8s linear;
  border-radius: 4px;
  cursor: pointer;
}
.bol:hover {
  background: #e6e5e5;
}
.parent {
  display: flex;
  align-items: center;
}
.parent i {
  transition: color 0.8s linear;
}
.parent:hover i {
  color: #000;
}
.parent:hover {
  color: #000;
}
.parent:active i {
  color: #00b4b4;
}

.child-list {
  background: #fff;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  position: relative;
  /*transition: height 0.8s linear;*/
  height: 100%;
  max-height: 100%;
  padding: 5px;
  margin-top: 10px;
  border-radius: 4px;
}
.child-list li {
  position: relative;
  list-style-type: none;
  margin-left: 0;
  border-bottom: 1px solid #dedede;
  padding: 5px;
}
.child-list li:last-of-type {
  border-bottom: none;
}
.is-closed {
  max-height: 0;
}
.show {
  /*height: 100% !important;*/
  max-height: 100% !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}

.fade-enter,
.fade-leave-to
/* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}

.smooth-enter-active,
.smooth-leave-active {
  transition: opacity 0.5s;
  height: auto;
}
.smooth-enter,
.smooth-leave-to {
  opacity: 0;
}

.height-enter-active,
.height-leave-active {
  transition: height 0.5s;
  height: auto;
}
.height-enter,
.height-leave-to {
  height: 0;
}
.ship-head {
  display: flex;
  width: 100%;
}
.pallet {
  width: 50%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ship-number {
  width: calc(50% + 3px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media all and (max-width: 700px) {
  .pallet {
    width: 60%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ship-number {
    width: calc(60% + 3px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.fa-arrow-circle-down {
  margin-left: auto;
}

.w100 {
  width: 100%;
}
.dflex {
  display: flex;
}
.info {
  border-top: 1px solid #dedede;
  padding-top: 5px;
  margin-top: 5px;
}
.truck-no {
  margin-left: -5px;
}
.closeX {
  top: 0;
}
.pdfLink {
  color: #429da8;
  cursor: pointer;
  margin-bottom: 0;
}
.pdfLink:hover {
  color: #00b4b4;
}
.pdfLink:active {
  color: #00b4b4;
}
.lightbox {
  z-index: 10003 !important;
}
.text-muted a {
  color: #00b4b4;
}
.doc-image {
  width: 100%;
  height: auto;
  margin-bottom: 10px;
}
.edit-notes {
  display: flex;
}
.edit-notes button {
  margin-left: auto;
}

#cke_17 {
  display: none !important;
}
.form-control:disabled,
.form-control[readonly] {
  background-color: #f8f8f8;
  opacity: 1;
}

.custom-checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-size: 16px;
  user-select: none;
}

.custom-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.custom-checkbox .checkmark {
  position: relative;
  height: 20px;
  width: 20px;
  background-color: #f0f0f0;
  border-radius: 4px;
  border: 2px solid #dcdcdc; /* Neutral border color when unchecked */
  margin-right: 8px;
  transition: border-color 0.3s, background-color 0.3s;
}

.custom-checkbox input:checked ~ .checkmark {
  background-color: #00b4b4; /* Checkbox background color when checked */
  border-color: #00b4b4; /* Border color changes to 00b4b4 when checked */
}

.custom-checkbox .checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.custom-checkbox input:checked ~ .checkmark:after {
  display: block;
}

.custom-checkbox .checkmark:after {
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}


.ql-container{
  height: 300px!important;
  overflow-y: auto;
}




/*
.doc-wrapper:last-child {
  margin-bottom: 0 !important;
}
*/
</style>
<style>
.ql-container{
  height: 300px!important;
  overflow-y: auto;
}
.pixelsbg {
  background-color: #475059;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1950 1200'%3E%3Cdefs%3E%3CradialGradient id='a' cx='0' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23586470'/%3E%3Cstop offset='1' stop-color='%23586470' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='b' cx='1950' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23505a65'/%3E%3Cstop offset='1' stop-color='%23505a65' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='c' cx='975' cy='0' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%233f474f'/%3E%3Cstop offset='1' stop-color='%233f474f' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='d' cx='975' cy='1200' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23697887'/%3E%3Cstop offset='1' stop-color='%23697887' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='e' cx='0' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23475059'/%3E%3Cstop offset='1' stop-color='%23475059' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='f' cx='1950' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23383e45'/%3E%3Cstop offset='1' stop-color='%23383e45' stop-opacity='0'/%3E%3C/radialGradient%3E%3C/defs%3E%3Crect fill='%23474f58' x='149' y='-1' width='152' height='152'/%3E%3Crect fill='%23464f58' x='299' y='149' width='152' height='152'/%3E%3Crect fill='%23464e57' x='300' y='-1' width='151' height='151'/%3E%3Crect fill='%23454e56' x='449' y='299' width='152' height='152'/%3E%3Crect fill='%23454d56' x='450' y='149' width='151' height='151'/%3E%3Crect fill='%23444d55' x='450' y='-1' width='151' height='151'/%3E%3Crect fill='%23444c55' x='600' y='299' width='151' height='152'/%3E%3Crect fill='%23434b54' x='600' y='149' width='151' height='151'/%3E%3Crect fill='%23434b53' x='600' y='-1' width='151' height='151'/%3E%3Crect fill='%23424a53' x='749' y='449' width='152' height='152'/%3E%3Crect fill='%23424a52' x='750' y='299' width='151' height='151'/%3E%3Crect fill='%23414951' x='750' y='149' width='151' height='151'/%3E%3Crect fill='%23414951' x='750' y='-1' width='151' height='151'/%3E%3Crect fill='%23404850' x='899' y='599' width='152' height='152'/%3E%3Crect fill='%23404750' x='900' y='449' width='151' height='151'/%3E%3Crect fill='%233f474f' x='900' y='299' width='151' height='151'/%3E%3Crect fill='%233f464e' x='900' y='149' width='151' height='151'/%3E%3Crect fill='%233f464e' x='900' y='-1' width='151' height='151'/%3E%3Crect fill='%233e454d' x='1050' y='449' width='151' height='152'/%3E%3Crect fill='%233e454c' x='1050' y='299' width='151' height='151'/%3E%3Crect fill='%233d444c' x='1050' y='149' width='151' height='151'/%3E%3Crect fill='%233d444b' x='1050' y='-1' width='151' height='151'/%3E%3Crect fill='%233c434b' x='1200' y='299' width='151' height='152'/%3E%3Crect fill='%233c424a' x='1200' y='149' width='151' height='151'/%3E%3Crect fill='%233b4249' x='1200' y='-1' width='151' height='151'/%3E%3Crect fill='%233b4149' x='1350' y='299' width='151' height='152'/%3E%3Crect fill='%233a4148' x='1350' y='149' width='151' height='151'/%3E%3Crect fill='%233a4047' x='1350' y='-1' width='151' height='151'/%3E%3Crect fill='%23394047' x='1500' y='149' width='151' height='152'/%3E%3Crect fill='%23393f46' x='1500' y='-1' width='151' height='151'/%3E%3Crect fill='%23383f46' x='1650' y='-1' width='151' height='152'/%3E%3Crect fill='%23394047' x='1800' y='149' width='152' height='152'/%3E%3Crect fill='%233b4148' x='1649' y='150' width='152' height='151'/%3E%3Crect fill='%233c434a' x='1800' y='300' width='152' height='151'/%3E%3Crect fill='%233d444c' x='1649' y='300' width='152' height='151'/%3E%3Crect fill='%233f464e' x='1800' y='450' width='152' height='151'/%3E%3Crect fill='%2340474f' x='1800' y='600' width='152' height='151'/%3E%3Crect fill='%23414951' x='1649' y='450' width='152' height='151'/%3E%3Crect fill='%23434a53' x='1499' y='300' width='151' height='151'/%3E%3Crect fill='%23444c55' x='1800' y='750' width='152' height='151'/%3E%3Crect fill='%23454d57' x='1649' y='600' width='152' height='151'/%3E%3Crect fill='%23474f58' x='1499' y='450' width='151' height='151'/%3E%3Crect fill='%2348515a' x='1800' y='900' width='152' height='151'/%3E%3Crect fill='%2349525c' x='1649' y='750' width='152' height='151'/%3E%3Crect fill='%234b545e' x='1499' y='600' width='151' height='151'/%3E%3Crect fill='%234c5560' x='1349' y='450' width='151' height='151'/%3E%3Crect fill='%234d5761' x='1800' y='1049' width='152' height='151'/%3E%3Crect fill='%234f5963' x='1649' y='900' width='152' height='151'/%3E%3Crect fill='%23505a65' x='1499' y='750' width='151' height='151'/%3E%3Crect fill='%23515c67' x='1349' y='600' width='151' height='151'/%3E%3Crect fill='%23535d69' x='1199' y='450' width='151' height='151'/%3E%3Crect fill='%23545f6b' x='1649' y='1050' width='152' height='151'/%3E%3Crect fill='%2355616c' x='1499' y='1049' width='151' height='152'/%3E%3Crect fill='%2357626e' x='1499' y='900' width='151' height='150'/%3E%3Crect fill='%23586470' x='1349' y='1049' width='151' height='152'/%3E%3Crect fill='%235a6672' x='1349' y='899' width='151' height='151'/%3E%3Crect fill='%235b6774' x='1349' y='750' width='151' height='150'/%3E%3Crect fill='%235c6976' x='1199' y='1049' width='151' height='152'/%3E%3Crect fill='%235e6b78' x='1199' y='899' width='151' height='151'/%3E%3Crect fill='%235f6c7a' x='1199' y='749' width='151' height='151'/%3E%3Crect fill='%23616e7c' x='1199' y='600' width='151' height='150'/%3E%3Crect fill='%2362707d' x='1049' y='1049' width='151' height='152'/%3E%3Crect fill='%2363717f' x='1049' y='899' width='151' height='151'/%3E%3Crect fill='%23657381' x='1049' y='749' width='151' height='151'/%3E%3Crect fill='%23667583' x='1049' y='600' width='151' height='150'/%3E%3Crect fill='%23687685' x='899' y='899' width='151' height='152'/%3E%3Crect fill='%2348515a' x='-1' y='149' width='152' height='152'/%3E%3Crect fill='%2349525b' x='150' y='150' width='150' height='151'/%3E%3Crect fill='%234a535d' x='-1' y='300' width='152' height='151'/%3E%3Crect fill='%234b545e' x='150' y='300' width='151' height='151'/%3E%3Crect fill='%234c555f' x='-1' y='450' width='152' height='151'/%3E%3Crect fill='%234d5660' x='-1' y='600' width='152' height='151'/%3E%3Crect fill='%234d5862' x='150' y='450' width='151' height='151'/%3E%3Crect fill='%234e5963' x='300' y='300' width='150' height='151'/%3E%3Crect fill='%234f5a64' x='-1' y='750' width='152' height='151'/%3E%3Crect fill='%23505b65' x='150' y='600' width='151' height='151'/%3E%3Crect fill='%23515c67' x='300' y='450' width='151' height='151'/%3E%3Crect fill='%23525d68' x='-1' y='900' width='152' height='151'/%3E%3Crect fill='%23535e69' x='150' y='750' width='151' height='151'/%3E%3Crect fill='%23545f6a' x='300' y='600' width='151' height='151'/%3E%3Crect fill='%2355606c' x='450' y='450' width='151' height='151'/%3E%3Crect fill='%2356616d' x='-1' y='1050' width='152' height='151'/%3E%3Crect fill='%2357636e' x='150' y='900' width='151' height='151'/%3E%3Crect fill='%23586470' x='300' y='750' width='151' height='151'/%3E%3Crect fill='%23596571' x='450' y='600' width='151' height='151'/%3E%3Crect fill='%235a6672' x='600' y='450' width='150' height='151'/%3E%3Crect fill='%235b6773' x='150' y='1050' width='151' height='151'/%3E%3Crect fill='%235c6875' x='300' y='1049' width='151' height='152'/%3E%3Crect fill='%235c6976' x='300' y='900' width='151' height='150'/%3E%3Crect fill='%235d6a77' x='450' y='1049' width='151' height='152'/%3E%3Crect fill='%235e6b79' x='450' y='899' width='152' height='151'/%3E%3Crect fill='%235f6d7a' x='450' y='750' width='152' height='150'/%3E%3Crect fill='%23606e7b' x='600' y='1049' width='151' height='152'/%3E%3Crect fill='%23616f7c' x='600' y='899' width='151' height='151'/%3E%3Crect fill='%2362707e' x='600' y='749' width='151' height='151'/%3E%3Crect fill='%2363717f' x='600' y='600' width='151' height='150'/%3E%3Crect fill='%23647280' x='750' y='1049' width='151' height='152'/%3E%3Crect fill='%23657382' x='750' y='899' width='150' height='151'/%3E%3Crect fill='%23667583' x='750' y='749' width='151' height='151'/%3E%3Crect fill='%23677684' x='750' y='600' width='150' height='150'/%3E%3Crect fill='%23687786' x='900' y='1050' width='150' height='151'/%3E%3Crect fill='%23475059' x='-1' y='-1' width='151' height='151'/%3E%3Crect fill='%23697887' x='900' y='750' width='150' height='150'/%3E%3Crect fill='%23383e45' x='1800' y='-1' width='151' height='151'/%3E%3Cg fill-opacity='.5'%3E%3Crect fill='url(%23a)' width='1950' height='1200'/%3E%3Crect fill='url(%23b)' width='1950' height='1200'/%3E%3Crect fill='url(%23c)' width='1950' height='1200'/%3E%3Crect fill='url(%23d)' width='1950' height='1200'/%3E%3Crect fill='url(%23e)' width='1950' height='1200'/%3E%3Crect fill='url(%23f)' width='1950' height='1200'/%3E%3C/g%3E%3C/svg%3E");
  background-attachment: fixed;
  background-size: cover;
  background-color: #475059;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1950 1200'%3E%3Cdefs%3E%3CradialGradient id='a' cx='0' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23586470'/%3E%3Cstop offset='1' stop-color='%23586470' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='b' cx='1950' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%233c768a'/%3E%3Cstop offset='1' stop-color='%233c768a' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='c' cx='975' cy='0' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23305e6e'/%3E%3Cstop offset='1' stop-color='%23305e6e' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='d' cx='975' cy='1200' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23697887'/%3E%3Cstop offset='1' stop-color='%23697887' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='e' cx='0' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23475059'/%3E%3Cstop offset='1' stop-color='%23475059' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='f' cx='1950' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%231a8182'/%3E%3Cstop offset='1' stop-color='%231a8182' stop-opacity='0'/%3E%3C/radialGradient%3E%3C/defs%3E%3Crect fill='%2346505a' x='149' y='-1' width='152' height='152'/%3E%3Crect fill='%2344515c' x='299' y='149' width='152' height='152'/%3E%3Crect fill='%2343515d' x='300' y='-1' width='151' height='151'/%3E%3Crect fill='%2341525e' x='449' y='299' width='152' height='152'/%3E%3Crect fill='%23405260' x='450' y='149' width='151' height='151'/%3E%3Crect fill='%233e5361' x='450' y='-1' width='151' height='151'/%3E%3Crect fill='%233d5462' x='600' y='299' width='151' height='152'/%3E%3Crect fill='%233c5463' x='600' y='149' width='151' height='151'/%3E%3Crect fill='%233a5565' x='600' y='-1' width='151' height='151'/%3E%3Crect fill='%23395666' x='749' y='449' width='152' height='152'/%3E%3Crect fill='%23375767' x='750' y='299' width='151' height='151'/%3E%3Crect fill='%23365969' x='750' y='149' width='151' height='151'/%3E%3Crect fill='%23345a6a' x='750' y='-1' width='151' height='151'/%3E%3Crect fill='%23335b6b' x='899' y='599' width='152' height='152'/%3E%3Crect fill='%23325d6c' x='900' y='449' width='151' height='151'/%3E%3Crect fill='%23305e6e' x='900' y='299' width='151' height='151'/%3E%3Crect fill='%232f606f' x='900' y='149' width='151' height='151'/%3E%3Crect fill='%232d6170' x='900' y='-1' width='151' height='151'/%3E%3Crect fill='%232c6372' x='1050' y='449' width='151' height='152'/%3E%3Crect fill='%232b6573' x='1050' y='299' width='151' height='151'/%3E%3Crect fill='%23296774' x='1050' y='149' width='151' height='151'/%3E%3Crect fill='%23286975' x='1050' y='-1' width='151' height='151'/%3E%3Crect fill='%23266b77' x='1200' y='299' width='151' height='152'/%3E%3Crect fill='%23256d78' x='1200' y='149' width='151' height='151'/%3E%3Crect fill='%23246f79' x='1200' y='-1' width='151' height='151'/%3E%3Crect fill='%2322727a' x='1350' y='299' width='151' height='152'/%3E%3Crect fill='%2321747c' x='1350' y='149' width='151' height='151'/%3E%3Crect fill='%2320767d' x='1350' y='-1' width='151' height='151'/%3E%3Crect fill='%231e797e' x='1500' y='149' width='151' height='152'/%3E%3Crect fill='%231d7c80' x='1500' y='-1' width='151' height='151'/%3E%3Crect fill='%231b7e81' x='1650' y='-1' width='151' height='152'/%3E%3Crect fill='%231c8083' x='1800' y='149' width='152' height='152'/%3E%3Crect fill='%231d8083' x='1649' y='150' width='152' height='151'/%3E%3Crect fill='%231f7f84' x='1800' y='300' width='152' height='151'/%3E%3Crect fill='%23217e85' x='1649' y='300' width='152' height='151'/%3E%3Crect fill='%23227e85' x='1800' y='450' width='152' height='151'/%3E%3Crect fill='%23247d86' x='1800' y='600' width='152' height='151'/%3E%3Crect fill='%23267c87' x='1649' y='450' width='152' height='151'/%3E%3Crect fill='%23287c87' x='1499' y='300' width='151' height='151'/%3E%3Crect fill='%23297b88' x='1800' y='750' width='152' height='151'/%3E%3Crect fill='%232b7a88' x='1649' y='600' width='152' height='151'/%3E%3Crect fill='%232d7a88' x='1499' y='450' width='151' height='151'/%3E%3Crect fill='%232f7989' x='1800' y='900' width='152' height='151'/%3E%3Crect fill='%23317989' x='1649' y='750' width='152' height='151'/%3E%3Crect fill='%23337889' x='1499' y='600' width='151' height='151'/%3E%3Crect fill='%2335788a' x='1349' y='450' width='151' height='151'/%3E%3Crect fill='%2337778a' x='1800' y='1049' width='152' height='151'/%3E%3Crect fill='%233a778a' x='1649' y='900' width='152' height='151'/%3E%3Crect fill='%233c768a' x='1499' y='750' width='151' height='151'/%3E%3Crect fill='%233e768a' x='1349' y='600' width='151' height='151'/%3E%3Crect fill='%2340758a' x='1199' y='450' width='151' height='151'/%3E%3Crect fill='%2343758a' x='1649' y='1050' width='152' height='151'/%3E%3Crect fill='%2345758a' x='1499' y='1049' width='151' height='152'/%3E%3Crect fill='%2347758a' x='1499' y='900' width='151' height='150'/%3E%3Crect fill='%234a758a' x='1349' y='1049' width='151' height='152'/%3E%3Crect fill='%234c748a' x='1349' y='899' width='151' height='151'/%3E%3Crect fill='%234e748a' x='1349' y='750' width='151' height='150'/%3E%3Crect fill='%2351748a' x='1199' y='1049' width='151' height='152'/%3E%3Crect fill='%2354758a' x='1199' y='899' width='151' height='151'/%3E%3Crect fill='%2356758a' x='1199' y='749' width='151' height='151'/%3E%3Crect fill='%23597589' x='1199' y='600' width='151' height='150'/%3E%3Crect fill='%235b7589' x='1049' y='1049' width='151' height='152'/%3E%3Crect fill='%235e7689' x='1049' y='899' width='151' height='151'/%3E%3Crect fill='%23617688' x='1049' y='749' width='151' height='151'/%3E%3Crect fill='%23637788' x='1049' y='600' width='151' height='150'/%3E%3Crect fill='%23667787' x='899' y='899' width='151' height='152'/%3E%3Crect fill='%2348515a' x='-1' y='149' width='152' height='152'/%3E%3Crect fill='%2349525c' x='150' y='150' width='150' height='151'/%3E%3Crect fill='%234a535d' x='-1' y='300' width='152' height='151'/%3E%3Crect fill='%234b545e' x='150' y='300' width='151' height='151'/%3E%3Crect fill='%234c565f' x='-1' y='450' width='152' height='151'/%3E%3Crect fill='%234d5761' x='-1' y='600' width='152' height='151'/%3E%3Crect fill='%234e5862' x='150' y='450' width='151' height='151'/%3E%3Crect fill='%234f5963' x='300' y='300' width='150' height='151'/%3E%3Crect fill='%23505a64' x='-1' y='750' width='152' height='151'/%3E%3Crect fill='%23515b66' x='150' y='600' width='151' height='151'/%3E%3Crect fill='%23515c67' x='300' y='450' width='151' height='151'/%3E%3Crect fill='%23525d68' x='-1' y='900' width='152' height='151'/%3E%3Crect fill='%23535e69' x='150' y='750' width='151' height='151'/%3E%3Crect fill='%2354606b' x='300' y='600' width='151' height='151'/%3E%3Crect fill='%2355616c' x='450' y='450' width='151' height='151'/%3E%3Crect fill='%2356626d' x='-1' y='1050' width='152' height='151'/%3E%3Crect fill='%2357636f' x='150' y='900' width='151' height='151'/%3E%3Crect fill='%23586470' x='300' y='750' width='151' height='151'/%3E%3Crect fill='%23596571' x='450' y='600' width='151' height='151'/%3E%3Crect fill='%235a6672' x='600' y='450' width='150' height='151'/%3E%3Crect fill='%235b6774' x='150' y='1050' width='151' height='151'/%3E%3Crect fill='%235c6875' x='300' y='1049' width='151' height='152'/%3E%3Crect fill='%235d6a76' x='300' y='900' width='151' height='150'/%3E%3Crect fill='%235e6b78' x='450' y='1049' width='151' height='152'/%3E%3Crect fill='%235f6c79' x='450' y='899' width='152' height='151'/%3E%3Crect fill='%23606d7a' x='450' y='750' width='152' height='150'/%3E%3Crect fill='%23616e7b' x='600' y='1049' width='151' height='152'/%3E%3Crect fill='%23626f7d' x='600' y='899' width='151' height='151'/%3E%3Crect fill='%2362707e' x='600' y='749' width='151' height='151'/%3E%3Crect fill='%2363717f' x='600' y='600' width='151' height='150'/%3E%3Crect fill='%23647281' x='750' y='1049' width='151' height='152'/%3E%3Crect fill='%23657482' x='750' y='899' width='150' height='151'/%3E%3Crect fill='%23667583' x='750' y='749' width='151' height='151'/%3E%3Crect fill='%23677684' x='750' y='600' width='150' height='150'/%3E%3Crect fill='%23687786' x='900' y='1050' width='150' height='151'/%3E%3Crect fill='%23475059' x='-1' y='-1' width='151' height='151'/%3E%3Crect fill='%23697887' x='900' y='750' width='150' height='150'/%3E%3Crect fill='%231a8182' x='1800' y='-1' width='151' height='151'/%3E%3Cg fill-opacity='0.79'%3E%3Crect fill='url(%23a)' width='1950' height='1200'/%3E%3Crect fill='url(%23b)' width='1950' height='1200'/%3E%3Crect fill='url(%23c)' width='1950' height='1200'/%3E%3Crect fill='url(%23d)' width='1950' height='1200'/%3E%3Crect fill='url(%23e)' width='1950' height='1200'/%3E%3Crect fill='url(%23f)' width='1950' height='1200'/%3E%3C/g%3E%3C/svg%3E");
  background-attachment: fixed;
  background-size: cover;
}
.pixelsbggrid {
  background-color: #475059;
  background-size: cover;
  background-position: center;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1950 1200' preserveAspectRatio='xMidYMid slice'%3E%3Cdefs%3E%3CradialGradient id='a' cx='0' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%2356626d'/%3E%3Cstop offset='1' stop-color='%2356626d' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='b' cx='1950' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%2337778a'/%3E%3Cstop offset='1' stop-color='%2337778a' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='c' cx='975' cy='0' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%232b5c60'/%3E%3Cstop offset='1' stop-color='%232b5c60' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='d' cx='975' cy='1200' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23667787'/%3E%3Cstop offset='1' stop-color='%23667787' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='e' cx='0' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23475059'/%3E%3Cstop offset='1' stop-color='%23475059' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='f' cx='1950' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%231a8182'/%3E%3Cstop offset='1' stop-color='%231a8182' stop-opacity='0'/%3E%3C/radialGradient%3E%3ClinearGradient id='base' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23475059'/%3E%3Cstop offset='.5' stop-color='%2356626d'/%3E%3Cstop offset='1' stop-color='%231a8182'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='%23475059' width='1950' height='1200'/%3E%3Crect fill='url(%23base)' width='1950' height='1200'/%3E%3Cline x1='0' y1='0' x2='0' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='50' y1='0' x2='50' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='100' y1='0' x2='100' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='150' y1='0' x2='150' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='200' y1='0' x2='200' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='250' y1='0' x2='250' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='300' y1='0' x2='300' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='350' y1='0' x2='350' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='400' y1='0' x2='400' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='450' y1='0' x2='450' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='500' y1='0' x2='500' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='550' y1='0' x2='550' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='600' y1='0' x2='600' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='650' y1='0' x2='650' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='700' y1='0' x2='700' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='750' y1='0' x2='750' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='800' y1='0' x2='800' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='850' y1='0' x2='850' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='900' y1='0' x2='900' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='950' y1='0' x2='950' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1000' y1='0' x2='1000' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1050' y1='0' x2='1050' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1100' y1='0' x2='1100' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1150' y1='0' x2='1150' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1200' y1='0' x2='1200' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1250' y1='0' x2='1250' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1300' y1='0' x2='1300' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1350' y1='0' x2='1350' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1400' y1='0' x2='1400' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1450' y1='0' x2='1450' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1500' y1='0' x2='1500' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1550' y1='0' x2='1550' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1600' y1='0' x2='1600' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1650' y1='0' x2='1650' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1700' y1='0' x2='1700' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1750' y1='0' x2='1750' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1800' y1='0' x2='1800' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1850' y1='0' x2='1850' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1900' y1='0' x2='1900' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='0' x2='1950' y2='0' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='50' x2='1950' y2='50' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='100' x2='1950' y2='100' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='150' x2='1950' y2='150' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='200' x2='1950' y2='200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='250' x2='1950' y2='250' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='300' x2='1950' y2='300' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='350' x2='1950' y2='350' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='400' x2='1950' y2='400' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='450' x2='1950' y2='450' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='500' x2='1950' y2='500' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='550' x2='1950' y2='550' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='600' x2='1950' y2='600' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='650' x2='1950' y2='650' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='700' x2='1950' y2='700' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='750' x2='1950' y2='750' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='800' x2='1950' y2='800' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='850' x2='1950' y2='850' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='900' x2='1950' y2='900' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='950' x2='1950' y2='950' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1000' x2='1950' y2='1000' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='1050' x2='1950' y2='1050' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1100' x2='1950' y2='1100' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1150' x2='1950' y2='1150' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cg fill-opacity='0.45'%3E%3Crect fill='url(%23a)' width='1950' height='1200'/%3E%3Crect fill='url(%23b)' width='1950' height='1200'/%3E%3Crect fill='url(%23c)' width='1950' height='1200'/%3E%3Crect fill='url(%23d)' width='1950' height='1200'/%3E%3Crect fill='url(%23e)' width='1950' height='1200'/%3E%3Crect fill='url(%23f)' width='1950' height='1200'/%3E%3C/g%3E%3C/svg%3E");
}
.part-on-order {
  background-color: rgba(0, 180, 180, 0.18) !important;
}
.part-on-order-text {
  color: #000;
  font-weight: 500;
}


.pdfLink {
    color: #429da8;
    cursor: pointer;
    margin-bottom: 0;
}
/*.employee-wrap { max-width: 1600px; }*/

  .field-label { display: flex; align-items: center; gap: 5px; }
  .field-label .material-icons { font-size: 18px; }

  .nowrap { white-space: nowrap; }
  .col-qty { width: 60px; }
  .col-notes { min-width: 200px; }

  .notes-footer { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
  .notes-checks { display: flex; flex-direction: column; gap: 8px; }

  .release-chips { display: flex; flex-wrap: wrap; gap: 6px; max-height: 320px; overflow-y: auto; }
  .release-chip {
    padding: 3px 8px; border-radius: 4px; background: #f2f2f3;
    color: #6c757d; font-size: 13px; text-decoration: none;
    transition: background 0.2s;
  }
  .release-chip:hover { background: #e6e5e5; color: #6c757d; }
  .release-chip.current { background: #00b4b4; color: #fff; }
  .equal-col { display: flex; flex-direction: column; }
.fill-card { flex: 1 1 auto; }

/* Notes card: stack vertically and let the editor take the leftover space */
.notes-card { display: flex; flex-direction: column; }
.notes-form { display: flex; flex-direction: column; flex: 1 1 auto; }
.editor-group { display: flex; flex-direction: column; flex: 1 1 auto; }

.document-editor { display: flex; flex-direction: column; flex: 1 1 auto; }
.document-editor .ql-container {
  flex: 1 1 auto;
  height: auto !important;   /* overrides the fixed 300px */
  min-height: 200px;         /* so the editor never collapses */
  overflow-y: auto;
}
.document-editor .ql-editor { min-height: 100%; }
.col-actual { min-width: 180px; }
.superseded { text-decoration: line-through; opacity: 0.6; }
.actual-part { color: #000; font-weight: 500; }
.form-control.is-changed { border-color: #00b4b4; background: rgba(0, 180, 180, 0.06); }
.parts-footer { display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin-top: 10px; }
.lines-table .col-part   { width: 200px; min-width: 160px; }
.lines-table .col-actual { width: 200px; min-width: 180px; }

.lines-table .part-cell {
  white-space: normal;
  overflow-wrap: anywhere;   /* long part numbers wrap instead of spilling */
}

.lines-table .actual-cell .form-control {
  width: 100%;
  min-width: 0;
}

.lines-table td,
.lines-table th { vertical-align: middle; }
</style>